import { Role } from "@core/enums/Role";

export interface LanguageOption {
  code: string;
  nativeName: string;
  englishName: string;
}

export const DEFAULT_LANGUAGE_CODE = "en-US";

/** Matches BCP-47 style locale codes such as "en-US", "pt-BR", "nb-NO". */
const LOCALE_CODE_PATTERN = /^[a-z]{2,3}-[A-Za-z]{2,4}$/;

export const LANGUAGE_STORAGE_KEY = "selectedLanguage";

/**
 * Bootstrap only — Transloco needs at least one lang at app start.
 * Runtime selectable locales come from GET /language-translation/languages.
 */
export const TRANSLATED_LANGUAGE_CODES = new Set<string>([
  DEFAULT_LANGUAGE_CODE,
]);

export const TRANSLATED_LANGUAGE_CODE_LIST = Array.from(
  TRANSLATED_LANGUAGE_CODES,
);

export function isKnownLanguageCode(code: string): boolean {
  return LOCALE_CODE_PATTERN.test(code);
}

export function isTranslatedLanguage(code: string): boolean {
  return TRANSLATED_LANGUAGE_CODES.has(code);
}

export function resolveTranslocoLang(locale: string): string {
  return isKnownLanguageCode(locale) ? locale : DEFAULT_LANGUAGE_CODE;
}

/**
 * Prefer a valid BCP-47 locale; otherwise previous (n-1); otherwise English.
 * Catalog load (and the languages API list) decide real availability.
 */
export function resolveLocaleFallback(
  requestedLocale: string,
  previousLocale: string,
): string {
  if (isKnownLanguageCode(requestedLocale)) {
    return requestedLocale;
  }

  if (
    previousLocale !== requestedLocale &&
    isKnownLanguageCode(previousLocale)
  ) {
    return previousLocale;
  }

  return DEFAULT_LANGUAGE_CODE;
}

/**
 * Candidates tried in order when switching language: requested → previous → en-US.
 * Each candidate is loaded via the translations API; failures fall through.
 */
export function buildLocaleFallbackCandidates(
  requestedLocale: string,
  previousLocale: string,
): string[] {
  const candidates: string[] = [];
  const addCandidate = (locale: string) => {
    if (isKnownLanguageCode(locale) && !candidates.includes(locale)) {
      candidates.push(locale);
    }
  };

  addCandidate(requestedLocale);
  addCandidate(previousLocale);
  addCandidate(DEFAULT_LANGUAGE_CODE);

  return candidates;
}

export function getLanguageDisplayName(code: string): string {
  try {
    const displayNames = new Intl.DisplayNames([DEFAULT_LANGUAGE_CODE], {
      type: "language",
    });
    return displayNames.of(code.split("-")[0]) ?? code;
  } catch {
    return code;
  }
}

/** Same rule as Language Preference visibility in the header menu. */
export function canSwitchLanguage(roleId: number | null | undefined): boolean {
  if (roleId == null) {
    return false;
  }

  return (
    roleId !== Role.SUPER_ADMIN && roleId !== Role.GLOBAL_SERVICE_ENGINEER
  );
}
